import { useState } from "react";
import type { MediaItem } from "../api/types";
import { isReelMedia, isStoryMedia } from "../lib/media-views";
import { Icon, type IconName } from "./Icon";

/**
 * A post's cover, wherever a post is shown.
 *
 * Instagram's image addresses expire after a while, and a disconnected account cannot get
 * new ones: the old cards then showed a beige void with the browser's broken-image text on
 * top. When the picture does not load the cover becomes a designed card instead — the
 * post's own colour, its kind and the start of its caption — so it can still be recognised.
 */

const COVERS = [
  ["#c2683f", "#e4a985"],
  ["#6f4e37", "#c4a582"],
  ["#d4932c", "#f1d39b"],
  ["#4f7472", "#a9c4c0"],
  ["#7d7b45", "#cfcc9a"],
  ["#a8443a", "#e6a597"],
  ["#3e2c1e", "#9c7552"],
] as const;

/** The same post always gets the same colour: picked from its id, not from its position. */
function coverColours(id: string): readonly [string, string] {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return COVERS[hash % COVERS.length]!;
}

export function mediaKindIcon(media: Pick<MediaItem, "mediaType" | "mediaProductType"> | undefined): IconName {
  if (!media) return "message-circle";
  if (isStoryMedia(media as MediaItem)) return "circle-dashed";
  if (isReelMedia(media as MediaItem)) return "clapperboard";
  return media.mediaType === "CAROUSEL_ALBUM" ? "gallery-horizontal" : "image";
}

interface PostCoverProps {
  media: MediaItem | undefined;
  className?: string;
  /** Hides the caption on the fallback, for the tiny thumbnails where it would not fit. */
  compact?: boolean;
  alt?: string;
}

export function PostCover({ media, className = "", compact = false, alt = "" }: PostCoverProps) {
  const source = media ? media.thumbnailUrl || media.mediaUrl : "";
  const [failed, setFailed] = useState<string | null>(null);
  const showImage = Boolean(source) && failed !== source;
  const [from, to] = coverColours(media?.id ?? "inbox");
  const caption = (media?.caption ?? "").trim();

  return (
    <span
      className={`cs-cover ${className}`}
      data-fallback={showImage ? undefined : "true"}
      // The post's colour sits behind the picture too: while it loads, the card is already
      // its own instead of a grey hole.
      style={{ background: `linear-gradient(150deg, ${from}, ${to})` }}
    >
      {showImage ? (
        <img src={source} alt={alt} loading="lazy" onError={() => setFailed(source)} />
      ) : (
        <span className="cs-cover-fallback" aria-hidden="true">
          <Icon name={mediaKindIcon(media)} size={compact ? 16 : 22} />
          {!compact && caption ? <span className="cs-cover-caption">{caption}</span> : null}
        </span>
      )}
    </span>
  );
}

/**
 * A person's initial on a colour picked from their handle, so the same person always wears
 * the same one: a long list reads faster when the eye can latch onto colours.
 */
const AVATAR_TONES = [
  ["#f6e4d8", "#a4532f"],
  ["#ebead6", "#6f6d3c"],
  ["#f5e9cf", "#8f6218"],
  ["#dde7e5", "#3f5c5b"],
  ["#f3dcd7", "#8e3a31"],
  ["#efe3d3", "#6f4e37"],
] as const;

export function Avatar({ name, size = 40 }: { name: string; size?: number }) {
  const clean = name.replace(/^@+/, "").trim();
  let hash = 0;
  for (const char of clean) hash = (hash * 33 + char.charCodeAt(0)) >>> 0;
  const [background, color] = AVATAR_TONES[hash % AVATAR_TONES.length]!;
  const initial = /^instagram ·|^persona de/i.test(clean) ? "?" : (clean[0] ?? "?").toUpperCase();
  return (
    <span
      className="cs-avatar"
      aria-hidden="true"
      style={{ background, color, width: size, height: size, fontSize: Math.round(size * 0.42) }}
    >
      {initial}
    </span>
  );
}
